// Whether a pin falls outside the print sheet, and how faint it is drawn when
// it does.
//
// `PrintFrameLayers` dims everything outside the frame with a map layer, but
// pins are DOM markers riding above every layer, so the mask never touches
// them: a pin two kilometres off the paper looked exactly as printable as one
// in the middle of it. Each print surface therefore dims its own markers, and
// they share this module so the two cannot drift to different opacities —
// `TeamMapPrintView`'s pins and the editor's are the same pins to a hunter
// comparing the two screens.

import { pointInPolygon } from '../../lib/geo'
import type { GeoPt } from '../../lib/types'

/**
 * Opacity for a pin outside the sheet. Faint enough to read as excluded at a
 * glance, but still visible: it is a pin the hunter may want to bring INTO the
 * frame, so it has to stay findable — hiding it outright would leave them
 * panning blind.
 */
export const OUTSIDE_FRAME_PIN_OPACITY = 0.35

/**
 * The opacity a marker at `pt` renders at, given the frame ring (open or
 * closed) it should be judged against. No ring — no frame on screen, or a
 * surface that never draws one — means nothing is excluded, so everything
 * stays at full strength.
 */
export function pinOpacityForFrame(pt: GeoPt, frameRing: GeoPt[] | null | undefined): number {
  if (!frameRing || frameRing.length < 3) return 1
  // An anchor on the frame belongs to the sheet, matching the inclusive payload bounds.
  for (let i = 0; i < frameRing.length; i++) {
    const a = frameRing[i], b = frameRing[(i + 1) % frameRing.length]
    const cross = (pt.lon - a.lon) * (b.lat - a.lat) - (pt.lat - a.lat) * (b.lon - a.lon)
    if (Math.abs(cross) < 1e-12 && pt.lon >= Math.min(a.lon, b.lon) - 1e-12 && pt.lon <= Math.max(a.lon, b.lon) + 1e-12 && pt.lat >= Math.min(a.lat, b.lat) - 1e-12 && pt.lat <= Math.max(a.lat, b.lat) + 1e-12) return 1
  }
  return pointInPolygon(pt, frameRing) ? 1 : OUTSIDE_FRAME_PIN_OPACITY
}
